import { useCallback, useEffect, useRef } from 'react'
import { shapeSelectionSetsEqual } from '../helpers/selection-scope'
import { computeMultiSelectChrome } from '../helpers/multi-select-geometry'
import type { ShapeSelectionHistoryMeta } from '@/lib/shapes/types/shape-selection-history-meta'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useSelectionHistory(ctx: ShapeManagerStateApi) {
  const {
    multiSelectChromeRef,
    onShapeSelectionHistory,
    selectedShapeIds,
    selectedShapeIdsRef,
    shapesRef,
  } = ctx

  useEffect(() => {
    selectedShapeIdsRef.current = selectedShapeIds
  }, [selectedShapeIds, selectedShapeIdsRef])

  const onShapeSelectionHistoryRef = useRef(onShapeSelectionHistory)

  useEffect(() => {
    onShapeSelectionHistoryRef.current = onShapeSelectionHistory
  }, [onShapeSelectionHistory])

  const recordShapeSelectionHistory = useCallback(
    (before: string[], after: string[], meta?: ShapeSelectionHistoryMeta) => {
      if (shapeSelectionSetsEqual(before, after)) return

      const beforeChrome =
        before.length > 1 && multiSelectChromeRef.current
          ? { ...multiSelectChromeRef.current }
          : undefined
      const afterChrome =
        after.length > 1
          ? (computeMultiSelectChrome(after, shapesRef.current) ?? undefined)
          : undefined

      const payload = {
        beforeChrome,
        afterChrome,
        source: meta?.source ?? 'user',
        gesture: meta?.gesture ?? 'replace',
        historyTxnId: meta?.historyTxnId,
      } as const

      // `recordShapeSelectionHistory` is often called from `setSelectedShapeIds` updaters
      // (e.g. clearSelection with history). Invoking the parent `onShapeSelectionHistory`
      // synchronously would update HomeInner during ShapeManager's state update — React warns.
      queueMicrotask(() => {
        const cb = onShapeSelectionHistoryRef.current

        if (!cb) return
        cb(before, after, payload)
      })
    },
    [multiSelectChromeRef, shapesRef]
  )

  return { onShapeSelectionHistoryRef, recordShapeSelectionHistory }
}

export type SelectionHistoryApi = ReturnType<typeof useSelectionHistory>
